import { useCallback } from 'react';

import { Icon } from '@rbilabs/universal-components';
import { useFocusEffect, useRouter } from 'expo-router';
import { useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withRepeat,
  withSequence,
  withTiming,
} from 'react-native-reanimated';

import ActionButton, { ActionButtonSizes, ActionButtonVariants } from 'components/action-button';
import { routes } from 'navigation/routing';
import { primitive } from 'styles/constants/primitives';
import LocalStorage from 'utils/cognito/storage.web';
import { StorageKeys } from 'utils/local-storage';

const AnimatedView = Animated.createAnimatedComponent(View);

export const BottomLinks = ({
  loyaltyUserId,
  loading,
}: {
  loyaltyUserId?: string;
  loading: boolean;
}) => {
  const { formatMessage } = useIntl();
  const router = useRouter();
  const scale = useSharedValue(1);

  const startAnimation = useCallback(() => {
    const duration = 400;
    const startDelay = 500;
    scale.value = withDelay(
      startDelay,
      withRepeat(
        withSequence(
          withTiming(1.3, {
            duration,
            easing: Easing.out(Easing.ease),
          }),
          withTiming(1, {
            duration,
            easing: Easing.in(Easing.ease),
          })
        ),
        2
      )
    );
  }, [scale]);

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ scale: scale.value }],
  }));

  const handleShowAnimation = useCallback(() => {
    if (!loyaltyUserId || loading) {
      return;
    }

    try {
      const storedValue = LocalStorage.getItem(StorageKeys.LOYALTY_ONBOARDING_SEEN) || [];
      if (!storedValue.includes(loyaltyUserId)) {
        startAnimation();
        LocalStorage.setItem(StorageKeys.LOYALTY_ONBOARDING_SEEN, [...storedValue, loyaltyUserId]);
      }
    } catch (error) {
      LocalStorage.setItem(StorageKeys.LOYALTY_ONBOARDING_SEEN, []);
    }
  }, [startAnimation, loyaltyUserId, loading]);

  useFocusEffect(
    useCallback(() => {
      handleShowAnimation();
    }, [handleShowAnimation])
  );

  const handleViewHistory = () => {
    router.navigate(routes.history);
  };

  const handleViewHowItWorks = () => {
    router.navigate(routes.rewardsHowItWorks);
  };

  const howItWorksText = formatMessage({ id: 'howItWorks', defaultMessage: 'How it works' });
  const viewHistoryText = formatMessage({ id: 'viewHistory', defaultMessage: 'View History' });

  return (
    <View style={styles.bottomLinks} accessibilityRole="toolbar">
      <AnimatedView style={animatedStyle}>
        <ActionButton
          variant={ActionButtonVariants.TEXT_ONLY}
          size={ActionButtonSizes.SMALL}
          onPress={handleViewHowItWorks}
          style={styles.linkContainer}
          leftIcon={<Icon variant="info" size="$4" />}
          keepIconSize={true}
          padding={0}
        >
          {howItWorksText}
        </ActionButton>
      </AnimatedView>
      <ActionButton
        variant={ActionButtonVariants.TEXT_ONLY}
        size={ActionButtonSizes.SMALL}
        onPress={handleViewHistory}
        style={styles.linkContainer}
        padding={0}
      >
        {viewHistoryText}
      </ActionButton>
    </View>
  );
};

const styles = StyleSheet.create({
  bottomLinks: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: primitive.spacing.$5,
  },
  linkContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 0,
  },
  infoIconContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    height: 20,
    borderRadius: 10,
    paddingRight: primitive.spacing.$1,
  },
  linkText: {
    paddingRight: primitive.spacing.$1,
    fontSize: primitive.fontSizes.xs,
    fontFamily: primitive.fontFamily.$headers,
    color: primitive.bk.$bbqBrown,
  },
});
